/**
 * AUA HYDRATE — Cart drawer & AJAX cart
 * Uses the Shopify Cart Ajax API to add/update/remove items and re-render the drawer.
 *
 * Endpoints used:
 *   POST /cart/add.js
 *   POST /cart/change.js
 *   GET  /cart.js
 *   GET  /?section_id=cart-drawer-section  (if available — falls back to /cart.js)
 *
 * Public events emitted on document:
 *   - aua:cart:add { item, cart }
 *   - aua:cart:update { cart }
 *   - aua:cart:open
 *   - aua:cart:close
 */

(() => {
  if (typeof window === 'undefined') return;

  // ============================================
  // I18N (asset JS is not Liquid-processed — detect storefront language
  // from <html lang> and pick the matching UI strings)
  // ============================================
  const AUA_LANG = (document.documentElement.getAttribute('lang') || 'fr').slice(0, 2).toLowerCase();
  const AUA_I18N = {
    fr: {"shipDone": "Livraison express offerte — c'est gagné !", "shipRemaining": "Plus que {amount} pour la livraison offerte", "addError": "Erreur ajout au panier", "empty": "Votre panier est vide.", "discover": "Découvrir les arômes", "decrease": "Diminuer", "increase": "Augmenter", "remove": "Retirer", "noteAdd": "Ajouter une note", "noteOptional": "facultatif", "noteAria": "Note pour la commande (facultatif)", "notePlaceholder": "Une précision pour nos préparateurs ? (ex. instructions de livraison)", "subtotal": "Sous-total", "youSave": "Vous économisez", "upsellTitle": "Complétez votre trio", "upsellAdd": "Ajouter", "discountAdd": "Code promo", "discountPlaceholder": "Votre code", "discountApply": "Appliquer", "discountInvalid": "Code invalide ou non applicable", "discountRemove": "Retirer le code", "taxesNote": "📦 Livraison express en 1-2 jours", "checkout": "Procéder au paiement", "paymentTrust": "Paiement sécurisé — TWINT et Klarna acceptés", "adding": "Ajout en cours…", "addFail": "Impossible d'ajouter au panier. Réessayez."},
    de: {"shipDone": "Express-Versand gratis — geschafft!", "shipRemaining": "Nur noch {amount} bis zum Gratis-Versand", "addError": "Fehler beim Hinzufügen zum Warenkorb", "empty": "Ihr Warenkorb ist leer.", "discover": "Sorten entdecken", "decrease": "Verringern", "increase": "Erhöhen", "remove": "Entfernen", "noteAdd": "Notiz hinzufügen", "noteOptional": "optional", "noteAria": "Notiz zur Bestellung (optional)", "notePlaceholder": "Ein Hinweis für unser Team? (z. B. Lieferhinweise)", "subtotal": "Zwischensumme", "youSave": "Sie sparen", "upsellTitle": "Machen Sie Ihr Trio komplett", "upsellAdd": "Hinzufügen", "discountAdd": "Rabattcode", "discountPlaceholder": "Ihr Code", "discountApply": "Anwenden", "discountInvalid": "Code ungültig oder nicht anwendbar", "discountRemove": "Code entfernen", "taxesNote": "📦 Express-Lieferung in 1-2 Tagen", "checkout": "Zur Zahlung", "paymentTrust": "Sichere Zahlung — TWINT & Klarna akzeptiert", "adding": "Wird hinzugefügt…", "addFail": "Hinzufügen zum Warenkorb nicht möglich. Bitte erneut versuchen."},
    it: {"shipDone": "Spedizione express gratuita — obiettivo raggiunto!", "shipRemaining": "Ancora {amount} per la spedizione gratuita", "addError": "Errore durante l'aggiunta al carrello", "empty": "Il tuo carrello è vuoto.", "discover": "Scopri i gusti", "decrease": "Riduci", "increase": "Aumenta", "remove": "Rimuovi", "noteAdd": "Aggiungi una nota", "noteOptional": "facoltativo", "noteAria": "Nota per l'ordine (facoltativo)", "notePlaceholder": "Una precisazione per il nostro team? (es. istruzioni di consegna)", "subtotal": "Subtotale", "youSave": "Risparmi", "upsellTitle": "Completa il tuo trio", "upsellAdd": "Aggiungi", "discountAdd": "Codice sconto", "discountPlaceholder": "Il tuo codice", "discountApply": "Applica", "discountInvalid": "Codice non valido o non applicabile", "discountRemove": "Rimuovi il codice", "taxesNote": "📦 Consegna express in 1-2 giorni", "checkout": "Procedi al pagamento", "paymentTrust": "Pagamento sicuro — TWINT e Klarna accettati", "adding": "Aggiunta in corso…", "addFail": "Impossibile aggiungere al carrello. Riprova."},
    es: {"shipDone": "Envío exprés gratis — ¡conseguido!", "shipRemaining": "Solo {amount} más para el envío gratis", "addError": "Error al añadir al carrito", "empty": "Tu carrito está vacío.", "discover": "Descubrir los sabores", "decrease": "Reducir", "increase": "Aumentar", "remove": "Quitar", "noteAdd": "Añadir una nota", "noteOptional": "opcional", "noteAria": "Nota para el pedido (opcional)", "notePlaceholder": "¿Alguna indicación para nuestro equipo? (p. ej. instrucciones de entrega)", "subtotal": "Subtotal", "youSave": "Ahorras", "upsellTitle": "Completa tu trío", "upsellAdd": "Añadir", "discountAdd": "Código de descuento", "discountPlaceholder": "Tu código", "discountApply": "Aplicar", "discountInvalid": "Código no válido o no aplicable", "discountRemove": "Quitar el código", "taxesNote": "📦 Entrega exprés en 1-2 días", "checkout": "Proceder al pago", "paymentTrust": "Pago seguro — TWINT y Klarna aceptados", "adding": "Añadiendo…", "addFail": "No se ha podido añadir al carrito. Inténtalo de nuevo."},
    en: {"shipDone": "Free express shipping — unlocked!", "shipRemaining": "Only {amount} to go for free shipping", "addError": "Error adding to cart", "empty": "Your cart is empty.", "discover": "Discover the flavors", "decrease": "Decrease", "increase": "Increase", "remove": "Remove", "noteAdd": "Add a note", "noteOptional": "optional", "noteAria": "Order note (optional)", "notePlaceholder": "Anything we should know? (e.g. delivery instructions)", "subtotal": "Subtotal", "youSave": "You save", "upsellTitle": "Complete your trio", "upsellAdd": "Add", "discountAdd": "Discount code", "discountPlaceholder": "Your code", "discountApply": "Apply", "discountInvalid": "Code invalid or not applicable", "discountRemove": "Remove code", "taxesNote": "📦 Express delivery in 1-2 days", "checkout": "Proceed to payment", "paymentTrust": "Secure payment — TWINT & Klarna accepted", "adding": "Adding…", "addFail": "Couldn't add to cart. Please try again."}
  };

  // Surcharges du marché européen. TWINT est un moyen de paiement suisse et
  // n'existe pas au checkout UE ; la livraison n'y est pas express Poste
  // suisse mais standard en 3-5 jours. Seules les clés qui changent sont
  // redéfinies, le reste vient de la table ci-dessus.
  const AUA_I18N_EU = {
    fr: {"shipDone": "Livraison offerte — c'est gagné !", "taxesNote": "📦 Livraison en 3-5 jours", "paymentTrust": "Paiement sécurisé — Klarna et PayPal acceptés"},
    de: {"shipDone": "Gratis Versand — geschafft!", "taxesNote": "📦 Lieferung in 3-5 Tagen", "paymentTrust": "Sichere Zahlung — Klarna & PayPal akzeptiert"},
    it: {"shipDone": "Spedizione gratuita — obiettivo raggiunto!", "taxesNote": "📦 Consegna in 3-5 giorni", "paymentTrust": "Pagamento sicuro — Klarna e PayPal accettati"},
    es: {"shipDone": "Envío gratis — ¡conseguido!", "taxesNote": "📦 Entrega en 3-5 días", "paymentTrust": "Pago seguro — Klarna y PayPal aceptados"},
    en: {"shipDone": "Free shipping unlocked!", "taxesNote": "📦 Delivery in 3-5 days", "paymentTrust": "Secure payment — Klarna & PayPal accepted"}
  };

  // Le script est chargé en `defer` : le DOM est parsé, l'attribut est là.
  const AUA_MARKET = (() => {
    const el = document.querySelector('[data-cart-drawer]');
    return (el && el.dataset.market) || 'ch';
  })();

  const T = Object.assign(
    {},
    AUA_I18N[AUA_LANG] || AUA_I18N.fr,
    AUA_MARKET === 'ch' ? {} : (AUA_I18N_EU[AUA_LANG] || AUA_I18N_EU.fr)
  );

  const SELECTORS = {
    drawer: '[data-cart-drawer]',
    open: '[data-cart-open]',
    close: '[data-drawer-close]',
    items: '[data-cart-items]',
    item: '[data-cart-item]',
    qtyValue: '[data-qty-value]',
    qtyBtn: '[data-cart-qty]',
    remove: '[data-cart-remove]',
    body: '[data-cart-body]',
    footer: '[data-cart-footer]',
    total: '[data-cart-total]',
    empty: '[data-cart-empty]',
    productForm: 'form[action*="/cart/add"]',
    headerCount: '.aua-header__cart-count',
    headerCart: '.aua-header__cart',
  };

  // Configuration injectée par cart-drawer.liquid sur la racine du tiroir.
  // Le seuil de port offert et le format monétaire dépendent du MARCHÉ
  // (CHF 50.– en Suisse, 79 € en Europe) : impossible de les figer dans cet
  // asset statique, que Liquid ne rend pas. La racine [data-cart-drawer] est
  // le bon porteur : renderDrawer ne réécrit que le corps et le pied, jamais
  // elle, donc les valeurs survivent à tous les re-rendus.
  // Les valeurs par défaut reproduisent le marché suisse, pour que le tiroir
  // reste correct même si l'attribut disparaît.
  const drawerConfig = () => {
    const el = document.querySelector(SELECTORS.drawer);
    const threshold = parseInt(el && el.dataset.shipThreshold, 10);
    return {
      threshold: Number.isFinite(threshold) && threshold > 0 ? threshold : 5000,
      prefix: (el && el.dataset.moneyPrefix) || 'CHF ',
      suffix: (el && el.dataset.moneySuffix) || '',
      decimal: (el && el.dataset.moneyDecimal) || '.',
    };
  };

  // Le séparateur décimal vient lui aussi de Liquid : point en Suisse
  // (« CHF 29.90 »), virgule probable en euros. Le tiroir est d'abord rendu en
  // Liquid puis réécrit ici ; si les deux ne s'accordent pas, les montants
  // changent de ponctuation dès que le client touche aux quantités.
  // Carte variante -> prix barre (centimes), rendue par Liquid dans le tiroir.
  // Elle existe parce que /cart.js n'expose PAS compare_at_price (verifie le
  // 27.08.2026). Une variante absente retombe sur son prix courant : jamais de
  // faux barre, l'economie est au pire SOUS-evaluee.
  const COMPARE_MAP = (() => {
    try {
      const el = document.querySelector('[data-aua-compare-map]');
      return el ? JSON.parse(el.textContent) : {};
    } catch (e) {
      return {};
    }
  })();

  // Total « plein » du panier : somme des prix barres unitaires (ou du prix
  // courant a defaut) x quantites. Compare a cart.total_price, la difference
  // couvre a la fois les prix barres ET un eventuel code promo.
  const fullTotalOf = (cart) => cart.items.reduce((sum, it) => {
    const unit = Number(COMPARE_MAP[String(it.variant_id)]) || it.price;
    return sum + unit * it.quantity;
  }, 0);

  const moneyFormatter = (cents) => {
    const cfg = drawerConfig();
    const n = (parseInt(cents, 10) / 100).toFixed(2).replace('.', cfg.decimal);
    return cfg.prefix + n + cfg.suffix;
  };

  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));

  // Payment icon markup — cloned from the hidden <template> that
  // cart-drawer.liquid renders via payment_type_svg_tag (a Liquid-only
  // filter, unavailable to this static asset).
  const paymentTrustIconsHtml = () => {
    const tpl = document.getElementById('aua-payment-trust-icons');
    return tpl ? tpl.innerHTML : '';
  };

  // Progression vers la livraison offerte.
  // Lu sur [data-cart-drawer] à chaque appel (voir drawerConfig) : CHF 50.– en
  // Suisse, 79 € en Europe. Doit rester aligné sur la condition TOTAL_PRICE du
  // profil de livraison Shopify ET sur `assign ship_threshold` de
  // cart-drawer.liquid, qui est la source de l'attribut.
  const shipThreshold = () => drawerConfig().threshold;

  // Confettis du franchissement. Les particules sont créées à la volée puis
  // supprimées : rien ne traîne dans le DOM tant qu'il ne se passe rien.
  // Couleurs = les trois arômes, pour que la fête reste dans la marque.
  const CONFETTI_COLORS = ['#e8a8ae', '#f0b59b', '#f4e08c', '#c97e85'];

  const burstConfetti = (bar) => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    // La couche existe dans les DEUX gabarits (celui de cart-drawer.liquid et
    // celui reconstruit par renderDrawer plus bas). On la recrée quand même si
    // elle manque : c'est exactement l'oubli qui a fait disparaître les
    // confettis la première fois, et il ne doit pas pouvoir se reproduire.
    let layer = bar.querySelector('[data-ship-confetti]');
    if (!layer) {
      layer = document.createElement('span');
      layer.className = 'aua-ship-bar__confetti';
      layer.setAttribute('data-ship-confetti', '');
      layer.setAttribute('aria-hidden', 'true');
      bar.appendChild(layer);
    }
    layer.textContent = '';

    const COUNT = 18;
    for (let i = 0; i < COUNT; i++) {
      const p = document.createElement('i');
      // Réparties sur toute la largeur, avec un peu de hasard pour éviter
      // l'effet « peigne » d'une distribution parfaitement régulière.
      const startPct = (i / (COUNT - 1)) * 100 + (Math.random() * 8 - 4);
      const dx = (Math.random() * 120 - 60).toFixed(0);   // dérive latérale
      const peak = (-40 - Math.random() * 45).toFixed(0); // hauteur du jet
      const dy = (55 + Math.random() * 55).toFixed(0);    // retombée
      p.style.left = Math.max(0, Math.min(100, startPct)) + '%';
      p.style.background = CONFETTI_COLORS[i % CONFETTI_COLORS.length];
      p.style.setProperty('--dx', dx + 'px');
      p.style.setProperty('--peak', peak + 'px');
      p.style.setProperty('--dy', dy + 'px');
      p.style.setProperty('--rot', (Math.random() * 720 - 360).toFixed(0) + 'deg');
      p.style.animationDelay = (Math.random() * 90).toFixed(0) + 'ms';
      layer.appendChild(p);
    }
    setTimeout(() => { layer.textContent = ''; }, 1600);
  };

  // Mémorise l'état du seuil pour ne fêter que le FRANCHISSEMENT, jamais le
  // simple affichage d'un panier déjà complet.
  //
  // L'état de départ est lu sur la page AU CHARGEMENT, pas au premier appel :
  //   - panier vide ou sous 50 → false, donc un ajout direct d'un Duo à 59.80
  //     déclenche bien la fête (c'est le cas qui manquait) ;
  //   - panier déjà au-dessus de 50 au chargement → true, donc pas de rejeu.
  // Sans cette initialisation, un client arrivant avec un panier vide n'avait
  // aucun état de référence et le premier ajout ne fêtait rien.
  const readInitialShipState = () => {
    const bar = document.querySelector('[data-ship-bar]');
    // Pas de barre = panier vide : on part de « pas encore atteint ».
    return bar ? bar.dataset.complete === 'true' : false;
  };
  let shipWasComplete = readInitialShipState();
  document.addEventListener('DOMContentLoaded', () => {
    shipWasComplete = readInitialShipState();
  });

  const updateShipBar = (totalCents) => {
    const bar = document.querySelector('[data-ship-bar]');
    // Panier vidé : la barre disparaît. On retient l'état pour que le
    // prochain ajout, même directement au-dessus de 50.–, soit bien un
    // franchissement.
    if (!bar) { shipWasComplete = false; return; }
    const total = parseInt(totalCents, 10) || 0;
    const remaining = shipThreshold() - total;
    const complete = remaining <= 0;

    bar.dataset.complete = complete ? 'true' : 'false';

    if (shipWasComplete === false && complete) {
      // Retirer puis reposer l'attribut force le navigateur à rejouer
      // l'animation même si le nœud n'a pas été remplacé entre-temps.
      bar.removeAttribute('data-celebrate');
      void bar.offsetWidth;
      bar.setAttribute('data-celebrate', '');
      burstConfetti(bar);
      setTimeout(() => bar.removeAttribute('data-celebrate'), 1600);
    }
    shipWasComplete = complete;

    const text = bar.querySelector('[data-ship-bar-text]');
    if (text) {
      text.textContent = complete
        ? T.shipDone
        : T.shipRemaining.replace('{amount}', moneyFormatter(remaining));
    }

    const fill = bar.querySelector('[data-ship-bar-fill]');
    if (fill) {
      const pct = Math.max(0, Math.min(100, (total / shipThreshold()) * 100));
      fill.style.width = pct + '%';
    }
  };

  // ============================================
  // DRAWER OPEN / CLOSE
  // ============================================
  let lastFocused = null;

  const openDrawer = () => {
    const drawer = $(SELECTORS.drawer);
    if (!drawer) return;
    lastFocused = document.activeElement;
    drawer.hidden = false;
    requestAnimationFrame(() => {
      drawer.setAttribute('data-open', '');
      drawer.setAttribute('scroll-lock', '');
    });
    document.dispatchEvent(new CustomEvent('aua:cart:open'));
    // Resync du chip code promo a chaque ouverture : le rendu Liquid initial ne
    // voit PAS cart.discount_codes (drop indisponible cote Liquid, verifie le
    // 27.08.2026 — les totaux Liquid, eux, integrent bien le code). Sans ce
    // fetch, un code deja attache montrerait encore le champ de saisie apres un
    // rechargement de page. Couvre aussi un code applique dans un autre onglet.
    fetch(cartUrl('/cart.js'), { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((cart) => {
        const zone = $('[data-discount-zone]', drawer);
        if (zone && !zone.contains(document.activeElement)) {
          zone.innerHTML = discountZoneHtml(cart);
        }
      })
      .catch(() => {});
    // Move focus to close button for keyboard users
    const closeBtn = $(SELECTORS.close, drawer);
    if (closeBtn) closeBtn.focus({ preventScroll: true });
  };

  const closeDrawer = () => {
    const drawer = $(SELECTORS.drawer);
    if (!drawer) return;
    drawer.removeAttribute('data-open');
    drawer.removeAttribute('scroll-lock');
    setTimeout(() => {
      drawer.hidden = true;
      if (lastFocused && lastFocused.focus) lastFocused.focus({ preventScroll: true });
    }, 500);
    document.dispatchEvent(new CustomEvent('aua:cart:close'));
  };

  // Bind triggers
  document.addEventListener('click', (e) => {
    const opener = e.target.closest(SELECTORS.open);
    const closer = e.target.closest(SELECTORS.close);
    const cartLink = e.target.closest(SELECTORS.headerCart);

    if (opener || (cartLink && !e.metaKey && !e.ctrlKey)) {
      e.preventDefault();
      openDrawer();
      return;
    }
    if (closer) {
      e.preventDefault();
      closeDrawer();
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      const drawer = $(SELECTORS.drawer);
      if (drawer && drawer.hasAttribute('data-open')) closeDrawer();
    }
  });

  // ============================================
  // CART API
  // ============================================
  /* Locale-aware endpoints. The Cart AJAX API answers in the language of the
     URL it is called on: hitting a bare "/cart.js" from /de/ returns the
     DEFAULT locale, so product titles silently flipped back to French as soon
     as the drawer re-rendered. Prefix every call with the active root. */
  const localeRoot = () => {
    const shopifyRoot = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    if (shopifyRoot) return shopifyRoot.replace(/\/$/, '');
    // Fallback: first path segment when it is a 2-letter locale code.
    const seg = window.location.pathname.split('/')[1];
    return /^[a-z]{2}(-[a-z]{2})?$/i.test(seg) ? '/' + seg : '';
  };
  const cartUrl = (path) => localeRoot() + path;

  const fetchCart = async () => {
    const res = await fetch(cartUrl('/cart.js'), { headers: { Accept: 'application/json' } });
    return res.json();
  };

  const addToCart = async (formData) => {
    const res = await fetch(cartUrl('/cart/add.js'), {
      method: 'POST',
      body: formData,
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || T.addError);
    }
    return res.json();
  };

  const changeLine = async (line, quantity) => {
    const res = await fetch(cartUrl('/cart/change.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line, quantity }),
    });
    return res.json();
  };

  // Persist the order note as a cart-level attribute. Shopify carries this
  // through to the order, where it is visible to order preparers in the admin.
  const updateNote = async (note) => {
    const res = await fetch(cartUrl('/cart/update.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ note }),
    });
    return res.json();
  };

  // Code promo — applique AU PANIER via update.js {discount} (verifie le
  // 27.08.2026 : total_price baisse immediatement, discount_codes expose
  // {code, applicable}). Un code inapplicable RESTE attache : on le retire
  // aussitot pour ne pas laisser un etat invisible au checkout.
  const applyDiscount = async (code) => {
    const res = await fetch(cartUrl('/cart/update.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: code }),
    });
    return res.json();
  };

  // Images de FORMAT (1/2/3 sachets, fond blanc) par SKU — rendues par Liquid.
  // SKU absent de la carte (futur pack) -> repli sur l'image produit.
  const BUNDLE_IMGS = (() => {
    try {
      const el = document.querySelector('[data-aua-bundle-imgs]');
      return el ? JSON.parse(el.textContent) : {};
    } catch (e) {
      return {};
    }
  })();

  // Aromes pour l'upsell pre-achat — rendus par Liquid dans le tiroir (titres
  // localises, prix dans la devise de la requete, variante Solo par SKU).
  const UPSELL_MAP = (() => {
    try {
      const el = document.querySelector('[data-aua-upsell-map]');
      return el ? JSON.parse(el.textContent) : [];
    } catch (e) {
      return [];
    }
  })();

  // Gabarit jumeau de la zone Liquid : aromes ABSENTS du panier, sous les
  // articles ; les 3 presents -> rien. Ids compares en chaines strictes.
  const upsellHtml = (cart) => {
    if (!UPSELL_MAP.length || !cart.items.length) return '';
    const inCart = new Set(cart.items.map((it) => String(it.product_id)));
    const missing = UPSELL_MAP.filter((u) => !inCart.has(String(u.productId)));
    if (!missing.length) return '';
    const cards = missing.map((u) => `
      <li class="aua-upsell__card">
        <a href="${u.url}" class="aua-upsell__image" aria-hidden="true" tabindex="-1">
          <img src="${u.image}" alt="" width="48" height="60" loading="lazy">
        </a>
        <span class="aua-upsell__meta">
          <span class="aua-upsell__title">${escapeHtml(u.title)}</span>
          <span class="aua-upsell__price">${moneyFormatter(u.priceCents)}</span>
        </span>
        <button type="button" class="aua-btn aua-btn--secondary aua-btn--sm" data-upsell-add="${u.variantId}">${T.upsellAdd}</button>
      </li>`).join('');
    return `
      <div class="aua-upsell" data-upsell-zone>
        <p class="aua-upsell__heading">${T.upsellTitle}</p>
        <ul class="aua-upsell__list">${cards}</ul>
      </div>`;
  };

  const appliedCodeOf = (cart) =>
    ((cart.discount_codes || []).find((d) => d.applicable) || {}).code || '';

  // Gabarit jumeau de la zone Liquid (cart-drawer.liquid) — a garder d'accord.
  const discountZoneHtml = (cart) => {
    const code = appliedCodeOf(cart);
    if (code) {
      return `
        <div class="aua-discount__applied">
          <span class="aua-discount__chip">
            ${escapeHtml(code)}
            <button type="button" class="aua-discount__remove" data-discount-remove aria-label="${T.discountRemove}">×</button>
          </span>
        </div>`;
    }
    return `
      <details class="aua-ordernote">
        <summary class="aua-ordernote__summary">
          <span class="aua-ordernote__label">${T.discountAdd}</span>
          <svg class="aua-ordernote__chevron" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" aria-hidden="true"><path d="M4 6l4 4 4-4"/></svg>
        </summary>
        <div class="aua-discount__row">
          <input class="aua-discount__input" data-discount-input type="text" autocomplete="off" autocapitalize="characters" spellcheck="false" placeholder="${T.discountPlaceholder}" aria-label="${T.discountAdd}">
          <button type="button" class="aua-btn aua-btn--secondary aua-btn--sm" data-discount-apply>${T.discountApply}</button>
        </div>
        <p class="aua-discount__error" data-discount-error hidden>${T.discountInvalid}</p>
      </details>`;
  };

  // ============================================
  // RENDER
  // ============================================
  const renderDrawer = (cart) => {
    const drawer = $(SELECTORS.drawer);
    if (!drawer) return;

    const fullTotal = fullTotalOf(cart);

    const body = $(SELECTORS.body, drawer);
    const footer = $(SELECTORS.footer, drawer);

    if (cart.item_count === 0) {
      // Empty state
      body.innerHTML = `
        <div class="aua-drawer__empty" data-cart-empty>
          <div class="aua-drawer__empty-mark aua-italic">aua.</div>
          <p>${T.empty}</p>
          <a href="/collections/tous-les-aromes" class="aua-btn aua-btn--secondary">${T.discover}</a>
        </div>`;
      if (footer) footer.remove();
      return;
    }

    // Item list
    const itemsHtml = cart.items.map((item, i) => {
      const line = i + 1;
      const bundleImg = BUNDLE_IMGS[item.sku];
      const imgUrl = bundleImg || (item.image ? item.image.replace(/_(\d+)x\./, '_200x.') : null);
      const imgCls = bundleImg ? ' aua-cart-item__image--bundle' : '';
      const variantLine = item.product_has_only_default_variant
        ? ''
        : `<span class="aua-cart-item__variant">${escapeHtml(item.variant_title || '')}</span>`;
      // Barre par ligne — meme logique que le sous-total (COMPARE_MAP, repli
      // sur le prix courant : jamais de faux barre). Gabarit jumeau du Liquid.
      const lineUnit = Number(COMPARE_MAP[String(item.variant_id)]) || item.price;
      const lineFull = lineUnit * item.quantity;
      const lineWas = lineFull > item.final_line_price
        ? `<s class="aua-cart-item__price-was">${moneyFormatter(lineFull)}</s>`
        : '';
      return `
        <li class="aua-cart-item" data-cart-item data-line="${line}" data-key="${item.key}">
          <a href="${item.url}" class="aua-cart-item__image${imgCls}" aria-hidden="true">
            ${imgUrl ? `<img src="${imgUrl}" alt="" width="80" height="100" loading="lazy">` : `<div class="aua-cart-item__placeholder"></div>`}
          </a>
          <div class="aua-cart-item__body">
            <a href="${item.url}" class="aua-cart-item__title">${escapeHtml(item.product_title)}</a>
            ${variantLine}
            <div class="aua-cart-item__row">
              <div class="aua-cart-item__qty">
                <button type="button" class="aua-cart-item__qty-btn" data-cart-qty="-1" data-line="${line}" aria-label="${T.decrease}">−</button>
                <span class="aua-cart-item__qty-value" data-qty-value>${item.quantity}</span>
                <button type="button" class="aua-cart-item__qty-btn" data-cart-qty="1" data-line="${line}" aria-label="${T.increase}">+</button>
              </div>
              <span class="aua-cart-item__price-group">${lineWas}<span class="aua-cart-item__price">${moneyFormatter(item.final_line_price)}</span></span>
            </div>
            <button type="button" class="aua-cart-item__remove" data-cart-remove data-line="${line}">${T.remove}</button>
          </div>
        </li>`;
    }).join('');

    // Barre de livraison offerte : rendue ICI depuis le 13.09.2026, au-dessus des
    // produits (gabarit jumeau de cart-drawer.liquid). Elle DOIT faire partie de
    // cette chaine : body.innerHTML est reecrit a chaque mutation du panier, une
    // barre laissee dans le pied disparaitrait au premier changement de quantite.
    // updateShipBar() la retrouve par document.querySelector, ou qu'elle soit.
    const shipBarHtml = `
          <div class="aua-ship-bar" data-ship-bar data-complete="false">
            <p class="aua-ship-bar__label">
              <svg class="aua-ship-bar__check" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M3 8l3 3 7-7" stroke-linecap="round" stroke-linejoin="round"/></svg>
              <span data-ship-bar-text></span>
            </p>
            <span class="aua-ship-bar__track" aria-hidden="true"><span class="aua-ship-bar__fill" data-ship-bar-fill style="width: 0%"></span></span>
            <span class="aua-ship-bar__confetti" data-ship-confetti aria-hidden="true"></span>
          </div>
`;
    body.innerHTML = shipBarHtml + `<ul class="aua-cart-items" data-cart-items>${itemsHtml}</ul>` + upsellHtml(cart);

    // Rebuild footer if it doesn't exist
    if (!footer) {
      const panel = $('[data-drawer-panel]', drawer);
      // 13.09.2026 : le pied ne contient plus ni la barre de livraison (remontee
      // dans le corps), ni la note de commande, ni le code promo (supprimes a la
      // demande de l'utilisateur pour degager de la place aux produits et a l'upsell).
      panel.insertAdjacentHTML('beforeend', `
        <footer class="aua-drawer__footer" data-cart-footer>
          <div class="aua-drawer__totals">
            <span class="aua-fineprint">${T.subtotal}</span>
            <span class="aua-drawer__total-group">
              <s class="aua-drawer__total-compare" data-cart-total-compare${fullTotal > cart.total_price ? '' : ' hidden'}>${fullTotal > cart.total_price ? moneyFormatter(fullTotal) : ''}</s>
              <span class="aua-drawer__total" data-cart-total>${moneyFormatter(cart.total_price)}</span>
            </span>
          </div>
          <p class="aua-drawer__savings" data-cart-savings${fullTotal > cart.total_price ? '' : ' hidden'}>${fullTotal > cart.total_price ? `${T.youSave} ${moneyFormatter(fullTotal - cart.total_price)}` : ''}</p>
          <p class="aua-fineprint aua-drawer__note">${T.taxesNote}</p>
          <a href="/checkout" class="aua-btn aua-btn--primary aua-btn--lg aua-cart-drawer__checkout" data-cart-checkout>
            ${T.checkout}
            <span class="aua-btn__icon" aria-hidden="true">
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4"><path d="M3 8h10M9 4l4 4-4 4"/></svg>
            </span>
          </a>
          <div class="aua-payment-trust">
            <span class="aua-payment-trust__icons">${paymentTrustIconsHtml()}</span>
            <span class="aua-payment-trust__text">${T.paymentTrust}</span>
          </div>
        </footer>`);
    } else {
      $(SELECTORS.total, footer).textContent = moneyFormatter(cart.total_price);
      // Le barre et l'economie doivent suivre chaque mutation, pas seulement la
      // reconstruction complete — sinon ils figent sur l'etat d'ouverture.
      const cmp = $('[data-cart-total-compare]', footer);
      const sav = $('[data-cart-savings]', footer);
      const showDeal = fullTotal > cart.total_price;
      if (cmp) {
        cmp.hidden = !showDeal;
        cmp.textContent = showDeal ? moneyFormatter(fullTotal) : '';
      }
      if (sav) {
        sav.hidden = !showDeal;
        sav.textContent = showDeal ? `${T.youSave} ${moneyFormatter(fullTotal - cart.total_price)}` : '';
      }
      // La zone code promo suit l'etat du panier (chip si un code applicable est
      // attache, champ sinon) — sauf pendant la saisie : on ne regenere pas la
      // zone sous les doigts de l'utilisateur si le focus y est.
      const zone = $('[data-discount-zone]', footer);
      if (zone && !zone.contains(document.activeElement)) {
        zone.innerHTML = discountZoneHtml(cart);
      }
    }

    // Refresh the free-shipping progress bar (the footer exists by now).
    updateShipBar(cart.total_price);
  };

  const updateHeaderCount = (count) => {
    const headerCart = $(SELECTORS.headerCart);
    if (!headerCart) return;
    let badge = headerCart.querySelector(SELECTORS.headerCount);

    if (count > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'aua-header__cart-count';
        headerCart.appendChild(badge);
      }
      badge.textContent = count;
      badge.setAttribute('data-pulse', '');
      setTimeout(() => badge.removeAttribute('data-pulse'), 700);
    } else if (badge) {
      badge.remove();
    }
  };

  const escapeHtml = (str) => {
    const div = document.createElement('div');
    div.textContent = str || '';
    return div.innerHTML;
  };

  // ============================================
  // EVENT HANDLERS
  // ============================================

  // Quantity buttons inside drawer
  document.addEventListener('click', async (e) => {
    const qty = e.target.closest(SELECTORS.qtyBtn);
    const remove = e.target.closest(SELECTORS.remove);

    if (qty) {
      e.preventDefault();
      const line = parseInt(qty.dataset.line, 10);
      const delta = parseInt(qty.dataset.cartQty, 10);
      const valueEl = qty.parentElement.querySelector(SELECTORS.qtyValue);
      const current = parseInt(valueEl.textContent.trim(), 10);
      const next = Math.max(0, current + delta);
      const cart = await changeLine(line, next);
      renderDrawer(cart);
      updateHeaderCount(cart.item_count);
      document.dispatchEvent(new CustomEvent('aua:cart:update', { detail: { cart } }));
    } else if (remove) {
      e.preventDefault();
      const line = parseInt(remove.dataset.line, 10);
      const cart = await changeLine(line, 0);
      renderDrawer(cart);
      updateHeaderCount(cart.item_count);
      document.dispatchEvent(new CustomEvent('aua:cart:update', { detail: { cart } }));
    }
  });

  // ---- Order note (optional) ----
  let noteTimer = null;
  let noteDirty = false;

  const saveNoteFrom = async (textarea) => {
    if (!textarea) return;
    clearTimeout(noteTimer);
    try {
      await updateNote(textarea.value);
      noteDirty = false;
    } catch (err) {
      console.error('[AUA cart] note', err);
    }
  };

  // Debounced save while typing
  // ---- Code promo : appliquer (clic ou Entree) et retirer ----
  const submitDiscount = async (zone) => {
    const input = $('[data-discount-input]', zone);
    const btn = $('[data-discount-apply]', zone);
    const err = $('[data-discount-error]', zone);
    const code = (input && input.value.trim()) || '';
    if (!code) return;
    if (btn) btn.disabled = true;
    try {
      const cart = await applyDiscount(code);
      const entry = (cart.discount_codes || []).find(
        (d) => d.code.toLowerCase() === code.toLowerCase()
      );
      if (entry && entry.applicable) {
        renderDrawer(cart);
        updateHeaderCount(cart.item_count);
        document.dispatchEvent(new CustomEvent('aua:cart:update', { detail: { cart } }));
      } else {
        // Le code refuse reste attache au panier : on le retire tout de suite,
        // sinon il trainerait jusqu'au checkout dans un etat invisible.
        await applyDiscount('');
        if (err) err.hidden = false;
        if (input) input.select();
      }
    } catch (_) {
      if (err) err.hidden = false;
    } finally {
      if (btn) btn.disabled = false;
    }
  };

  document.addEventListener('click', async (e) => {
    const up = e.target.closest('[data-upsell-add]');
    if (up) {
      up.disabled = true;
      try {
        const fd = new FormData();
        fd.append('id', up.dataset.upsellAdd);
        fd.append('quantity', '1');
        const item = await addToCart(fd);
        const cart = await fetchCart();
        renderDrawer(cart);
        updateHeaderCount(cart.item_count);
        document.dispatchEvent(new CustomEvent('aua:cart:add', { detail: { item, cart } }));
      } catch (err) {
        console.error('[AUA cart]', err);
        up.disabled = false;
      }
      return;
    }
    const apply = e.target.closest('[data-discount-apply]');
    if (apply) {
      await submitDiscount(apply.closest('[data-discount-zone]'));
      return;
    }
    const removeCode = e.target.closest('[data-discount-remove]');
    if (removeCode) {
      removeCode.disabled = true;
      const cart = await applyDiscount('');
      renderDrawer(cart);
      updateHeaderCount(cart.item_count);
      document.dispatchEvent(new CustomEvent('aua:cart:update', { detail: { cart } }));
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter') return;
    const input = e.target.closest('[data-discount-input]');
    if (!input) return;
    e.preventDefault();
    submitDiscount(input.closest('[data-discount-zone]'));
  });

  document.addEventListener('input', (e) => {
    const masque = e.target.closest('[data-discount-input]');
    if (masque) {
      const err = $('[data-discount-error]', masque.closest('[data-discount-zone]'));
      if (err) err.hidden = true;
    }
    const ta = e.target.closest('[data-cart-note]');
    if (!ta) return;
    noteDirty = true;
    clearTimeout(noteTimer);
    noteTimer = setTimeout(() => saveNoteFrom(ta), 600);
  });

  // Immediate save when the field loses focus
  document.addEventListener('focusout', (e) => {
    const ta = e.target.closest('[data-cart-note]');
    if (!ta || !noteDirty) return;
    saveNoteFrom(ta);
  });

  // Guarantee the note is saved before leaving for checkout
  document.addEventListener('click', async (e) => {
    const checkout = e.target.closest('[data-cart-checkout]');
    if (!checkout) return;
    const ta = $('[data-cart-note]');
    if (ta && noteDirty) {
      e.preventDefault();
      await saveNoteFrom(ta);
      window.location = checkout.href;
    }
  });

  // Intercept product form submissions
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest(SELECTORS.productForm);
    if (!form) return;
    e.preventDefault();

    const submitBtn = form.querySelector('button[type="submit"], input[type="submit"]');
    const originalText = submitBtn ? submitBtn.querySelector('[data-cta-text]')?.textContent : null;

    if (submitBtn) {
      submitBtn.disabled = true;
      const textEl = submitBtn.querySelector('[data-cta-text]');
      if (textEl) textEl.textContent = T.adding;
    }

    try {
      const formData = new FormData(form);
      const item = await addToCart(formData);
      const cart = await fetchCart();
      renderDrawer(cart);
      updateHeaderCount(cart.item_count);
      openDrawer();
      document.dispatchEvent(new CustomEvent('aua:cart:add', { detail: { item, cart } }));
    } catch (err) {
      console.error('[AUA cart]', err);
      alert(err.message || T.addFail);
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
        const textEl = submitBtn.querySelector('[data-cta-text]');
        if (textEl && originalText) textEl.textContent = originalText;
      }
    }
  });

  // Re-fetch cart on bfcache restoration (avoids stale state)
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) {
      fetchCart().then((cart) => {
        updateHeaderCount(cart.item_count);
        updateShipBar(cart.total_price);
      });
    }
  });
})();
